/**
 * THE ONE PLACE THE APP KNOWS HOW BIG THE WINDOW IS.
 *
 * Until Phase 18 it did not know at all. The shell was one 1920-wide frame, the only media query
 * in the tree was `prefers-reduced-motion`, and a phone rendered the desktop layout at true width:
 * a fixed 232px rail, two fixed side columns, and 161px left over for the game. The audit at
 * `docs/BRIEF-PHASES-18-24.md` section 0 has the numbers.
 *
 * Three widths, and two facts that are not widths:
 *
 *   phone    under 768px   one column, a bottom tab bar, the side rails stacked under the main panel
 *   tablet   768 to 1279   an icon rail; the side rails stack below 1024 and sit beside above it
 *   desktop  1280 and up   the frame as it was
 *
 *   stacked  under 1024: every screen's side rails go under its main panel instead of beside it
 *   touch    a coarse pointer, or any stacked width: controls grow to 44px and small type to 11px
 *
 * One `matchMedia` listener per query for the whole app, read through `useSyncExternalStore`, and
 * mirrored onto `<html data-vp data-stacked data-touch>` so `skin.css` can carry the rules that
 * belong in a stylesheet. Inline styles cannot be reached by a media query, and this codebase is
 * 1,800 of them, so `sz()` below is the lever for those: it is a function of the DEVICE, which does
 * not change while the page is open, so it is safe to call outside a render.
 */

import { useSyncExternalStore } from 'react'

export type Vp = 'phone' | 'tablet' | 'desktop'

export interface Viewport {
  vp: Vp
  /** Side rails go under the main panel rather than beside it. Under 1024px. */
  stacked: boolean
  /** A coarse pointer, or a stacked width. Controls and small type are sized for a thumb. */
  touch: boolean
  /** Under 900px tall: the nav drops its harbour plate so every route stays on screen. */
  short: boolean
  width: number
}

export const BP_PHONE = 768
export const BP_STACK = 1024
export const BP_DESK = 1280

const QUERIES = {
  phone: `(max-width: ${BP_PHONE - 1}px)`,
  stacked: `(max-width: ${BP_STACK - 1}px)`,
  desktop: `(min-width: ${BP_DESK}px)`,
  coarse: '(pointer: coarse)',
  short: '(max-height: 900px)',
} as const

const DESKTOP: Viewport = { vp: 'desktop', stacked: false, touch: false, short: false, width: 1920 }

const hasWindow = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
const mqs = hasWindow
  ? (Object.fromEntries(Object.entries(QUERIES).map(([k, q]) => [k, window.matchMedia(q)])) as Record<
      keyof typeof QUERIES,
      MediaQueryList
    >)
  : null

function read(): Viewport {
  if (!mqs) return DESKTOP
  const phone = mqs.phone.matches
  const stacked = mqs.stacked.matches
  const v: Viewport = {
    vp: phone ? 'phone' : mqs.desktop.matches ? 'desktop' : 'tablet',
    stacked,
    touch: mqs.coarse.matches || stacked,
    short: mqs.short.matches,
    width: window.innerWidth,
  }
  const ds = document.documentElement.dataset
  ds.vp = v.vp
  ds.stacked = v.stacked ? 'true' : 'false'
  ds.touch = v.touch ? 'true' : 'false'
  return v
}

let snap: Viewport = read()
const listeners = new Set<() => void>()

if (mqs) {
  const onChange = () => {
    snap = read()
    listeners.forEach((l) => l())
  }
  for (const mq of Object.values(mqs)) mq.addEventListener('change', onChange)
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

export function useViewport(): Viewport {
  return useSyncExternalStore(subscribe, () => snap, () => DESKTOP)
}

/** The current viewport outside a render. Read it for layout maths, never for gameplay. */
export function viewport(): Viewport {
  return snap
}

/**
 * A font size for an inline style: what it was on desktop, and no smaller than `min` on touch.
 *
 * The 8.5px eyebrow that reads as instrumentation on a monitor is unreadable on a phone held at
 * arm's length. This is the smallest honest fix for a codebase whose sizes are numbers in JSX
 * rather than classes a stylesheet could reach.
 */
export function sz(n: number, min = 11): number {
  return snap.touch ? Math.max(min, n) : n
}
